import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_AUDIT_LOGS } from '../../graphql';
import { GetAuditLogsResponse } from '../../types/graphql';
import { useAccount } from '../../context/AccountContext';

type Filter = 'all' | 'approval' | 'agent' | 'settings';

const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'approval', label: 'Approvals' },
  { id: 'agent', label: 'Agent actions' },
  { id: 'settings', label: 'Settings changes' },
];

const AREA: Record<Exclude<Filter, 'all'>, string> = {
  approval: 'Approval',
  agent: 'Agent',
  settings: 'Settings',
};

const LABELS: Record<string, string> = {
  urlShortening: 'link shortening',
  bitlyToken: 'Bitly token',
  firstCommentPrefix: 'first-comment prefix',
  utmCampaign: 'UTM campaign',
  techContactEmail: 'technical contact',
  timezone: 'timezone',
  name: 'name',
  publicEnabled: 'the public site',
  publicSiteEnabled: 'the public site',
  publicTitle: 'site title',
  publicTagline: 'tagline',
  publicSlug: 'site address',
  publicAccessPassword: 'site password',
  publicAccessProtected: 'password protection',
};

const SECRET = /token|password|secret|key|credential/i;

function payloadOf(payload: unknown): Record<string, unknown> | null {
  if (!payload) return null;
  if (typeof payload === 'string') {
    try {
      const parsed = JSON.parse(payload);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }
  return typeof payload === 'object' ? (payload as Record<string, unknown>) : null;
}

function labelFor(key: string) {
  return (
    LABELS[key] ||
    key
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .toLowerCase()
  );
}

function phrase(key: string, value: unknown): string {
  const label = labelFor(key);
  if (SECRET.test(key)) return `Updated ${label}`;
  if (typeof value === 'boolean') return value ? `Turned ${label} on` : `Turned ${label} off`;
  if (typeof value === 'string' && value.trim() && value.length < 80)
    return `Set ${label} to ${value.trim()}`;
  if (typeof value === 'number') return `Set ${label} to ${value}`;
  return `Updated ${label}`;
}

function sentence(tableName: string, action: string, payload: unknown): string {
  const data = payloadOf(payload);
  const keys = data
    ? Object.keys(data).filter(
        (key) => !['id', 'accountId', 'account_id', 'updatedAt', 'createdAt'].includes(key)
      )
    : [];
  if (keys.length === 1) return phrase(keys[0], data?.[keys[0]]);
  if (keys.length > 1) {
    const named = keys.slice(0, 3).map(labelFor);
    const rest = keys.length > 3 ? `, and ${keys.length - 3} more` : '';
    const verb = action === 'Deleted' ? 'Removed' : action === 'Created' ? 'Added' : 'Updated';
    return `${verb} ${named.join(', ')}${rest}`;
  }
  const verb = action === 'Deleted' ? 'Removed' : action === 'Created' ? 'Added' : 'Updated';
  return `${verb} ${tableName || 'a record'}`;
}

function kindOf(log: {
  tableName: string;
  action: string;
  payload: unknown;
  userEmail: string;
}): Exclude<Filter, 'all'> {
  if (!log.userEmail) return 'agent';
  const blob =
    `${log.tableName || ''} ${log.action || ''} ${JSON.stringify(log.payload || {})}`.toLowerCase();
  if (/publish|approv|schedule|override|atomiz/.test(blob)) return 'approval';
  return 'settings';
}

function whenLabel(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (date.toDateString() === now.toDateString()) return `Today ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return `Yesterday ${time}`;
  const day = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return date.getFullYear() === now.getFullYear() ? day : `${day}, ${date.getFullYear()}`;
}

export function EventAuditSettings() {
  const [filter, setFilter] = useState<Filter>('all');
  const { accountId, isLoading: accountsLoading } = useAccount();
  const { data, loading, error } = useQuery<GetAuditLogsResponse>(GET_AUDIT_LOGS, {
    variables: { accountId },
    skip: !accountId,
  });

  const rows = useMemo(() => {
    return (data?.auditLogs || []).map((log) => {
      const kind = kindOf(log);
      return {
        id: log.id,
        when: whenLabel(log.createdAt),
        what: sentence(log.tableName, log.action, log.payload),
        who: log.userEmail || 'An agent',
        area: AREA[kind],
        kind,
      };
    });
  }, [data?.auditLogs]);

  const shown = filter === 'all' ? rows : rows.filter((row) => row.kind === filter);

  if (accountsLoading || loading) return <p className="ink-lede">Loading…</p>;

  return (
    <div>
      <h1 className="ink-h1">Activity log</h1>
      <p className="ink-lede">
        Every change to this account, by people and agents. Approvals record the exact version and
        any override.
      </p>
      {error ? <p className="ink-lede">Could not load the activity log.</p> : null}
      <div className="ink-act-filters">
        {FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            className="ink-chip"
            data-active={filter === item.id ? 'true' : 'false'}
            onClick={() => setFilter(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="ink-act">
        {shown.length === 0 ? (
          <p className="ink-lede" style={{ marginTop: 28 }}>
            {rows.length === 0 ? 'No activity yet.' : 'Nothing in this view.'}
          </p>
        ) : (
          shown.map((row) => (
            <div className="ink-act-row" key={row.id}>
              <span className="ink-act-when">{row.when}</span>
              <div>
                <span className="ink-act-what">{row.what}</span>
                <span className="ink-act-who"> · {row.who}</span>
              </div>
              <span className="ink-act-area">{row.area}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
